import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import { Divider, Flex, VFlex } from '@/components/base';
import IconButton from '@/components/Button/IconButton';
import Spinner from '@/components/Common/Spinner';
import { Stage } from '@/components/Stage/Stage';
import Heading from '@/components/Typography/Heading';
import Typo from '@/components/Typography/Typo';
import { PageHeader } from '@/shared/ui';
import { customScrollbar } from '@/styles/style';
import { DATE_FORMAT, getTimezoneDayjs } from '@/utils/time';

import { RankingLineChart } from '../components/RankingLineChart';
import { RankingTable } from '../components/RankingTable';
import { useProductRankingFromSheets } from '../hooks/useProductRanking';

export const ProductRankingPage = () => {
  const { data, isLoading, error } = useProductRankingFromSheets();
  const [selectedDate, setSelectedDate] = useState<string>('');
  // const [candidtates, setCandidtates] = useState<string[]>([]);

  const dateList = useMemo(() => {
    return [
      ...new Set(
        data?.products
          .map((product) => product.crawlDate)
          .sort((a, b) => new Date(a).getTime() - new Date(b).getTime()) || [],
      ),
    ];
  }, [data]);

  const hasNextDate = useMemo(() => {
    return dateList.indexOf(selectedDate) < dateList.length - 1;
  }, [dateList, selectedDate]);

  const hasPrevDate = useMemo(() => {
    return dateList.indexOf(selectedDate) > 0;
  }, [dateList, selectedDate]);

  useEffect(() => {
    if (data?.products && data.products.length > 0) {
      const latestDate = data.products.reduce((max, product) => {
        return new Date(product.crawlDate) > new Date(max.crawlDate)
          ? product
          : max;
      }, data.products[0]);

      setSelectedDate(latestDate.crawlDate);
    }
  }, [data]);

  return (
    <Stage>
      <Stage.Scene
        status={
          !isLoading &&
          !error &&
          (!data || !data.products || data.products.length === 0)
        }
      >
        <LoadingContainer>
          <Typo type="body1">No product ranking data available</Typo>
        </LoadingContainer>
      </Stage.Scene>
      <Stage.Scene status={isLoading}>
        <LoadingContainer>
          <Spinner />
        </LoadingContainer>
      </Stage.Scene>
      <Stage.Scene status={!!error}>
        <LoadingContainer>
          <Typo type="body1">
            Error loading data: {(error as Error)?.message || 'Unknown error'}
          </Typo>
        </LoadingContainer>
      </Stage.Scene>
      <Stage.Scene default status={!!data} data={data}>
        {(data) => {
          return (
            <StyledContainer>
              <PageHeader title="Product Ranking" />
              <Divider />

              <VFlex g={24} padding="24px">
                <Flex f={1} style={{ width: '100%', paddingRight: 24 }}>
                  <RankingLineChart
                    key={selectedDate}
                    data={data.products}
                    selectedDate={selectedDate}
                  />
                </Flex>
                <VFlex g={16} padding="0px 24px">
                  <Flex g={8}>
                    <Heading type="h3">
                      Ranking as of{' '}
                      {getTimezoneDayjs(selectedDate).format(
                        DATE_FORMAT.base,
                      )}{' '}
                    </Heading>
                    <IconButton
                      icon={<LeftOutlined />}
                      outlined={false}
                      disabled={!hasPrevDate}
                      onClick={() => {
                        setSelectedDate(
                          dateList[dateList.indexOf(selectedDate) - 1],
                        );
                      }}
                    />
                    <IconButton
                      icon={<RightOutlined />}
                      outlined={false}
                      disabled={!hasNextDate}
                      onClick={() => {
                        setSelectedDate(
                          dateList[dateList.indexOf(selectedDate) + 1],
                        );
                      }}
                    />
                  </Flex>
                  <RankingTable
                    data={data.products}
                    selectedDate={selectedDate}
                  />
                </VFlex>
              </VFlex>
            </StyledContainer>
          );
        }}
      </Stage.Scene>
    </Stage>
  );
};

const StyledContainer = styled(VFlex)`
  height: 100vh;
  overflow: auto;
  ${customScrollbar};
`;

const LoadingContainer = styled(VFlex)`
  justify-content: center;
  align-items: center;
  min-height: 100vh;
`;
